if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileZoomCleanup = null;
      }

      hide() {
        this.teardownMobileZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      teardownMobileZoom() {
        if (typeof this.mobileZoomCleanup === 'function') this.mobileZoomCleanup();
        this.mobileZoomCleanup = null;
      }

      setupMobileZoom(media, container) {
        this.teardownMobileZoom();

        if (!window.matchMedia('(max-width: 749px)').matches || !media || media.tagName !== 'IMG' || !container) return;

        let scale = 1;
        let translateX = 0;
        let translateY = 0;
        let mode = null;
        let startScale = 1;
        let startTranslateX = 0;
        let startTranslateY = 0;
        let startDistance = 0;
        let startCenterX = 0;
        let startCenterY = 0;
        let focalX = 0;
        let focalY = 0;
        let startTouchX = 0;
        let startTouchY = 0;

        const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
        const distance = (touchA, touchB) => Math.hypot(touchB.clientX - touchA.clientX, touchB.clientY - touchA.clientY);
        const center = (touchA, touchB) => ({
          x: (touchA.clientX + touchB.clientX) / 2,
          y: (touchA.clientY + touchB.clientY) / 2,
        });

        const fitToViewport = () => {
          const viewportWidth = Math.max(1, container.clientWidth);
          const viewportHeight = Math.max(1, container.clientHeight);
          const naturalWidth = media.naturalWidth || 1;
          const naturalHeight = media.naturalHeight || 1;
          const ratio = naturalWidth / naturalHeight;

          let baseWidth = viewportWidth;
          let baseHeight = baseWidth / ratio;

          if (baseHeight > viewportHeight) {
            baseHeight = viewportHeight;
            baseWidth = baseHeight * ratio;
          }

          media.style.width = `${Math.round(baseWidth)}px`;
          media.style.height = `${Math.round(baseHeight)}px`;
        };

        const clampTranslation = () => {
          const viewportWidth = container.clientWidth;
          const viewportHeight = container.clientHeight;
          const scaledWidth = media.clientWidth * scale;
          const scaledHeight = media.clientHeight * scale;
          const maxX = Math.max(0, (scaledWidth - viewportWidth) / 2);
          const maxY = Math.max(0, (scaledHeight - viewportHeight) / 2);

          translateX = clamp(translateX, -maxX, maxX);
          translateY = clamp(translateY, -maxY, maxY);
        };

        const applyTransform = () => {
          clampTranslation();
          media.style.transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0) scale(${scale.toFixed(3)})`;
          media.classList.toggle('is-mobile-zoomed', scale > 1.01);
        };

        const resetZoom = () => {
          scale = 1;
          translateX = 0;
          translateY = 0;
          mode = null;
          fitToViewport();
          applyTransform();
        };

        const handleTouchStart = (event) => {
          if (event.touches.length >= 2) {
            event.preventDefault();
            const first = event.touches[0];
            const second = event.touches[1];
            const pinchCenter = center(first, second);
            const containerRect = container.getBoundingClientRect();

            mode = 'pinch';
            startDistance = Math.max(1, distance(first, second));
            startScale = scale;
            startTranslateX = translateX;
            startTranslateY = translateY;
            startCenterX = pinchCenter.x;
            startCenterY = pinchCenter.y;
            focalX = pinchCenter.x - (containerRect.left + containerRect.width / 2) - translateX;
            focalY = pinchCenter.y - (containerRect.top + containerRect.height / 2) - translateY;
            return;
          }

          if (event.touches.length === 1 && scale > 1.01) {
            event.preventDefault();
            mode = 'pan';
            startTouchX = event.touches[0].clientX;
            startTouchY = event.touches[0].clientY;
            startTranslateX = translateX;
            startTranslateY = translateY;
          }
        };

        const handleTouchMove = (event) => {
          if (event.touches.length >= 2 && mode === 'pinch') {
            event.preventDefault();
            const first = event.touches[0];
            const second = event.touches[1];
            const pinchCenter = center(first, second);
            const nextScale = clamp(startScale * (distance(first, second) / startDistance), 1, 4);
            const ratio = nextScale / Math.max(startScale, 0.001);

            scale = nextScale;
            translateX = startTranslateX + (pinchCenter.x - startCenterX) - focalX * (ratio - 1);
            translateY = startTranslateY + (pinchCenter.y - startCenterY) - focalY * (ratio - 1);
            applyTransform();
            return;
          }

          if (event.touches.length === 1 && mode === 'pan' && scale > 1.01) {
            event.preventDefault();
            translateX = startTranslateX + (event.touches[0].clientX - startTouchX);
            translateY = startTranslateY + (event.touches[0].clientY - startTouchY);
            applyTransform();
          }
        };

        const handleTouchEnd = (event) => {
          if (scale <= 1.01) {
            resetZoom();
            return;
          }

          if (event.touches.length === 1) {
            mode = 'pan';
            startTouchX = event.touches[0].clientX;
            startTouchY = event.touches[0].clientY;
            startTranslateX = translateX;
            startTranslateY = translateY;
          } else {
            mode = null;
          }
        };

        const handleResize = () => resetZoom();

        fitToViewport();
        applyTransform();

        media.addEventListener('touchstart', handleTouchStart, { passive: false });
        media.addEventListener('touchmove', handleTouchMove, { passive: false });
        media.addEventListener('touchend', handleTouchEnd, { passive: false });
        media.addEventListener('touchcancel', handleTouchEnd, { passive: false });
        window.addEventListener('resize', handleResize);

        this.mobileZoomCleanup = () => {
          media.removeEventListener('touchstart', handleTouchStart);
          media.removeEventListener('touchmove', handleTouchMove);
          media.removeEventListener('touchend', handleTouchEnd);
          media.removeEventListener('touchcancel', handleTouchEnd);
          window.removeEventListener('resize', handleResize);
          media.classList.remove('is-mobile-zoomed');
          media.style.removeProperty('width');
          media.style.removeProperty('height');
          media.style.removeProperty('transform');
        };
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        const isMobile = window.matchMedia('(max-width: 749px)').matches;

        if (isMobile) {
          if (container) {
            container.scrollTop = 0;
            container.scrollLeft = 0;
          }
          this.setupMobileZoom(activeMedia, container);
        } else {
          this.teardownMobileZoom();
          activeMedia.scrollIntoView();
          if (container) container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
